// Smart Upload Handler Service
// Routes uploads to appropriate processors based on context

import WSRUploadService from '../features/wsr/services/wsrUploadService';

interface UploadContext {
  page: string;
  title: string;
  description: string;
  expectedFileTypes: string[];
  analysisType: string;
  handler: string;
  instructions: string[];
  clientCode?: string;
}

interface UploadResult {
  success: boolean;
  message: string;
  data?: any;
  analysisType: string;
}

class SmartUploadHandler {
  private static instance: SmartUploadHandler;

  static getInstance(): SmartUploadHandler {
    if (!SmartUploadHandler.instance) {
      SmartUploadHandler.instance = new SmartUploadHandler();
    }
    return SmartUploadHandler.instance;
  }

  async handleUpload(file: File, context: UploadContext, clientCode?: string): Promise<UploadResult> {
    console.log(`🔄 Smart Upload Handler: Processing ${file.name} for ${context.handler}`);
    try {
      // Validate file type
      if (!this.isValidFileType(file, context.expectedFileTypes)) {
        throw new Error(`Invalid file type. Expected: ${context.expectedFileTypes.join(', ')}`);
      }

      // Route to appropriate handler
      switch (context.handler) {
        case 'wsrUpload':
          return await this.handleWSRUpload(file, clientCode);
        case 'architectureUpload':
          return await this.handleArchitectureUpload(file, clientCode);
        case 'codeUpload':
          return await this.handleCodeUpload(file, clientCode);
        case 'vmUpload':
          return await this.handleVMUpload(file, clientCode);
        case 'databaseUpload':
          return await this.handleDatabaseUpload(file, clientCode);
        case 'sqlStaticAnalysisUpload':
          return await this.handleSQLStaticAnalysisUpload(file, clientCode);
        case 'generalUpload':
        default:
          return await this.handleGeneralUpload(file, clientCode);
      }
    } catch (error) {
      console.error('Upload processing failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Upload processing failed',
        analysisType: context.analysisType
      };
    }
  }
  private async handleSQLStaticAnalysisUpload(file: File, clientCode?: string): Promise<UploadResult> {
    // Read file as text
    const sqlText = await file.text();
    // Use the same logic as the in-browser analyzeSQL (duplicate here for service use)
    function analyzeSQL(sql: string) {
      const observations = [];
      const lineCount = sql.split(/\r?\n/).length;
      if (lineCount > 500) {
        observations.push({
          title: 'Oversized Procedure Detected',
          evidence: `Procedure has ${lineCount} lines (recommended <500).`,
          why: 'Large procedures are hard to maintain, debug, and test.',
          recommendation: 'Break down into smaller, modular procedures.'
        });
      }
      const paramMatch = sql.match(/CREATE\s+PROCEDURE[\s\S]+?\(([^)]*)\)/i);
      if (paramMatch) {
        const paramCount = paramMatch[1].split(',').length;
        if (paramCount > 15) {
          observations.push({
            title: 'Parameter Bloating Detected',
            evidence: `Procedure has ${paramCount} parameters (recommended <15).`,
            why: 'Too many parameters indicate design issues and create maintenance challenges.',
            recommendation: 'Refactor to use parameter tables or configuration tables.'
          });
        }
      }
      if (!/TRY\s*\{/i.test(sql) && !/CATCH\s*\{/i.test(sql) && !/TRY\s*$/im.test(sql)) {
        observations.push({
          title: 'Missing Error Handling Detected',
          evidence: 'No TRY...CATCH blocks found.',
          why: 'Lack of error handling makes debugging and reliability difficult.',
          recommendation: 'Add TRY...CATCH blocks and proper error logging.'
        });
      }
      if (/\b(\d{2,}|\'[^']+\')\b/.test(sql)) {
        observations.push({
          title: 'Magic Values Detected',
          evidence: 'Found hardcoded numbers or strings in code.',
          why: 'Magic values make code hard to understand and maintain.',
          recommendation: 'Replace with named constants or parameters.'
        });
      }
      if (/WITH\s*\(NOLOCK\)/i.test(sql)) {
        observations.push({
          title: 'NOLOCK Usage Detected',
          evidence: 'Found WITH (NOLOCK) in queries.',
          why: 'NOLOCK can cause dirty reads and data inconsistency.',
          recommendation: 'Use proper isolation levels instead.'
        });
      }
      if (/CURSOR/i.test(sql)) {
        observations.push({
          title: 'Cursor Implementation Detected',
          evidence: 'Found CURSOR usage.',
          why: 'Cursors are slow and create performance bottlenecks.',
          recommendation: 'Replace with set-based operations.'
        });
      }
      if (/DELETE\s+FROM\s+\w+\s*;?$/im.test(sql)) {
        observations.push({
          title: 'Unsafe DELETE Operations Detected',
          evidence: 'DELETE statement without WHERE clause.',
          why: 'Can cause accidental full-table deletion.',
          recommendation: 'Always use WHERE clause in DELETE statements.'
        });
      }
      if (/\bGOTO\b/i.test(sql)) {
        observations.push({
          title: 'GOTO Statements Detected',
          evidence: 'Found GOTO in code.',
          why: 'GOTO leads to unstructured, hard-to-maintain code.',
          recommendation: 'Use structured control flow instead.'
        });
      }
      return observations;
    }
    const observations = analyzeSQL(sqlText);
    return {
      success: true,
      message: `SQL file "${file.name}" analyzed. Found ${observations.length} observations.`,
      data: {
        fileName: file.name,
        content: sqlText,
        observations
      },
      analysisType: 'SQL Stored Procedure Static Analysis'
    };
  }

  private isValidFileType(file: File, expectedTypes: string[]): boolean {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const mimeType = file.type.toLowerCase();
    
    return expectedTypes.some(type => {
      const lowerType = type.toLowerCase();
      return fileExtension === lowerType || 
             mimeType.includes(lowerType) ||
             (lowerType === 'md' && mimeType === 'text/markdown') ||
             (lowerType === 'txt' && mimeType === 'text/plain');
    });
  }

  private async handleWSRUpload(file: File, clientCode?: string): Promise<UploadResult> {
    console.log('📋 Processing WSR upload...');
    
    try {
      const wsrUploadService = WSRUploadService.getInstance();
      const analysis = await wsrUploadService.handleFileUpload(file);
      
      return {
        success: true,
        message: `WSR file "${file.name}" analyzed successfully. Found ${analysis.workAccomplished.length} accomplished items, ${analysis.nextWeekTargets.length} targets, and ${analysis.risks.length} risks.`,
        data: analysis,
        analysisType: 'Weekly Status Report Analysis'
      };
    } catch (error) {
      throw new Error(`WSR analysis failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async handleArchitectureUpload(file: File, clientCode?: string): Promise<UploadResult> {
    console.log('🏗️ Processing Architecture upload...');
    
    // Mock architecture analysis - replace with actual implementation
    await this.simulateProcessing(2000);
    
    const mockAnalysis = {
      fileType: this.getFileExtension(file.name),
      components: ['Database Layer', 'Application Layer', 'Presentation Layer'],
      technologies: ['SQL Server', 'ASP.NET', 'React'],
      patterns: ['Layered Architecture', 'Repository Pattern', 'MVC'],
      recommendations: [
        'Consider microservices architecture for better scalability',
        'Implement API Gateway for service orchestration',
        'Add caching layer for improved performance'
      ]
    };

    return {
      success: true,
      message: `Architecture document "${file.name}" analyzed successfully. Identified ${mockAnalysis.components.length} components and ${mockAnalysis.technologies.length} technologies.`,
      data: mockAnalysis,
      analysisType: 'Enterprise Architecture Analysis'
    };
  }

  private async handleCodeUpload(file: File, clientCode?: string): Promise<UploadResult> {
    console.log('💻 Processing Code upload...');
    
    // Mock code analysis - replace with actual implementation
    await this.simulateProcessing(1500);
    
    const mockAnalysis = {
      fileType: this.getFileExtension(file.name),
      linesOfCode: Math.floor(Math.random() * 1000) + 100,
      codeQuality: Math.floor(Math.random() * 30) + 70, // 70-100
      vulnerabilities: Math.floor(Math.random() * 5),
      suggestions: [
        'Reduce cyclomatic complexity in main functions',
        'Add error handling for database operations',
        'Implement input validation for user inputs'
      ]
    };

    return {
      success: true,
      message: `Code file "${file.name}" analyzed successfully. Quality score: ${mockAnalysis.codeQuality}%, found ${mockAnalysis.vulnerabilities} potential issues.`,
      data: mockAnalysis,
      analysisType: 'Static Code Analysis'
    };
  }

  private async handleVMUpload(file: File, clientCode?: string): Promise<UploadResult> {
    console.log('🖥️ Processing VM Profile upload...');
    
    // Mock VM analysis - replace with actual implementation
    await this.simulateProcessing(1000);
    
    const mockAnalysis = {
      vmCount: Math.floor(Math.random() * 10) + 1,
      totalCPU: Math.floor(Math.random() * 64) + 8,
      totalMemory: Math.floor(Math.random() * 256) + 32,
      utilizationAvg: Math.floor(Math.random() * 40) + 40, // 40-80%
      recommendations: [
        'Consider CPU optimization for high-usage VMs',
        'Memory allocation could be optimized',
        'Network bandwidth utilization is within normal range'
      ]
    };

    return {
      success: true,
      message: `VM profile "${file.name}" analyzed successfully. Found ${mockAnalysis.vmCount} VMs with ${mockAnalysis.utilizationAvg}% average utilization.`,
      data: mockAnalysis,
      analysisType: 'Virtual Machine Profile Analysis'
    };
  }

  private async handleDatabaseUpload(file: File, clientCode?: string): Promise<UploadResult> {
    console.log('🗄️ Processing Database upload...');
    
    // Mock database analysis - replace with actual implementation
    await this.simulateProcessing(2500);
    
    const mockAnalysis = {
      tableCount: Math.floor(Math.random() * 50) + 10,
      indexCount: Math.floor(Math.random() * 100) + 20,
      queryCount: Math.floor(Math.random() * 200) + 50,
      performanceScore: Math.floor(Math.random() * 30) + 60, // 60-90
      recommendations: [
        'Add indexes on frequently queried columns',
        'Optimize slow-running queries',
        'Consider table partitioning for large tables'
      ]
    };

    return {
      success: true,
      message: `Database file "${file.name}" analyzed successfully. Performance score: ${mockAnalysis.performanceScore}%, found ${mockAnalysis.tableCount} tables.`,
      data: mockAnalysis,
      analysisType: 'Database Analysis'
    };
  }

  private async handleGeneralUpload(file: File, clientCode?: string): Promise<UploadResult> {
    console.log('📄 Processing General upload...');
    
    // Mock general analysis - replace with actual implementation
    await this.simulateProcessing(1000);
    
    const fileType = this.getFileExtension(file.name);
    const mockAnalysis = {
      fileType,
      size: file.size,
      detectedContent: this.getContentType(fileType),
      extractedInfo: [
        'Document contains structured data',
        'Multiple sections identified',
        'Potential for automated processing'
      ]
    };

    return {
      success: true,
      message: `File "${file.name}" processed successfully. Detected as ${mockAnalysis.detectedContent}.`,
      data: mockAnalysis,
      analysisType: 'General File Analysis'
    };
  }

  private async simulateProcessing(delay: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  private getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || 'unknown';
  }

  private getContentType(extension: string): string {
    const contentTypes: { [key: string]: string } = {
      'pdf': 'PDF Document',
      'docx': 'Word Document', 
      'xlsx': 'Excel Spreadsheet',
      'pptx': 'PowerPoint Presentation',
      'md': 'Markdown Document',
      'txt': 'Text Document',
      'sql': 'SQL Script',
      'json': 'JSON Data',
      'xml': 'XML Document',
      'csv': 'CSV Data',
      'png': 'Image File',
      'jpg': 'Image File',
      'jpeg': 'Image File',
      'svg': 'Vector Image'
    };
    
    return contentTypes[extension] || 'Unknown File Type';
  }

  // Method to get upload statistics
  getUploadStats(): any {
    return {
      totalUploads: 0, // Would track in real implementation
      successRate: 95,
      averageProcessingTime: '1.2s',
      supportedFormats: 25
    };
  }
}

export default SmartUploadHandler;